import {useToast} from "vue-toastification";
import * as Sentry from "@sentry/browser";

/**
 * Sprawdza, czy podany obiekt posiada, odpowiednią strukturę.
 */
function objectHasStructure(object: any, structure: string[]): boolean {
    if (typeof object !== "object") {
        return false;
    }

    for (const key of structure) {
        if (!object.hasOwnProperty(key)) {
            return false;
        }
    }

    return true;
}

type IHandleErrorTypes =
    | "UNKNOWN_ERROR"
    | "BAD_REQUEST_ERROR"
    | "NOT_AUTHORIZED_ERROR"
    | "FORBIDDEN_ERROR"
    | "NOT_FOUND_ERROR"
    | "VALIDATION_ERROR"
    | "SERVER_ERROR"
    | "NETWORK_ERROR";

interface IHandledError {
    originalError: any;
    type: IHandleErrorTypes;
    message: string;
    code?: number;
    data?: Record<string, any>[];
    validationErrors?: Record<string, string[]>; // Tylko dla typu VALIDATION_ERROR
}

/**
 * Metoda pomocnicza do obsługi przechwyconego błędu z try/catch.
 * @param error
 */
export function handleError(error: any): IHandledError {
    const handledError = parseError(error);

    // Typy błędów, które nie powinny być wysyłane do Sentry.
    const ignoredErrorTypes: IHandleErrorTypes[] = [
        "VALIDATION_ERROR",
        "NETWORK_ERROR",
        "BAD_REQUEST_ERROR",
        "FORBIDDEN_ERROR",
        "NOT_AUTHORIZED_ERROR",
    ];
    if (!ignoredErrorTypes.includes(handledError.type)) {
        Sentry.captureException(handledError.originalError, {
            tags: {
                type: "HANDLE_ERROR",
            },
            contexts: {
                handledError: {
                    // Ponieważ klucz "type" jest zarezerwowany.
                    "handled type": handledError.type,
                    message: handledError.message,
                },
            },
        });
    }

    useToast().error(handledError.message);

    return handledError;
}

function parseError(error: any): IHandledError {
    const handledError: IHandledError = {
        originalError: error,
        type: "UNKNOWN_ERROR",
        message:
            "Wystąpił nieznany błąd. Jeśli błąd będzie się powtarzał, skontaktuj się przez formularz kontaktowy na stronie: zrsa.pl/kontakt",
    };

    if (error === null) {
        return handledError;
    }

    // Jeśli błąd nie jest obiektem, to sprawdzamy, czy jest to string.
    if (typeof error !== "object") {
        if (typeof error === "string") {
            handledError.message = error;
        }
        return handledError;
    }

    // Teraz wiemy, że błąd jest obiektem, ale nie jest typu Error.
    // Jeśli błąd posiada klucz "status", oznacza to, że zapytanie zostało wykonane, ale kod odpowiedzi nie jest 2xx.
    if (error.status) {
        if (objectHasStructure(error.data, ["message"])) {
            handledError.message = error.data.message;
        }

        switch (error.status) {
            case 400:
                handledError.type = "BAD_REQUEST_ERROR";
                break;
            case 401 || 419:
                handledError.type = "NOT_AUTHORIZED_ERROR";
                break;
            case 403:
                handledError.type = "FORBIDDEN_ERROR";
                break;
            case 404:
                handledError.type = "NOT_FOUND_ERROR";
                break;
            case 422:
                handledError.type = "BAD_REQUEST_ERROR";
                if (objectHasStructure(error.data, ["errors"])) {
                    handledError.type = "VALIDATION_ERROR";
                    handledError.validationErrors = error.data.errors;
                }
                break;
        }

        if (error.status >= 500) {
            handledError.type = "SERVER_ERROR";

            if (error.status === 503) {
                handledError.message = "Serwer jest tymczasowo niedostępny. Spróbuj ponownie za chwilę.";
            }
        }

        if (handledError.type !== "UNKNOWN_ERROR") {
            return handledError;
        }
    }

    // Jeśli utracono połączenie z internetem, to wyświetlamy odpowiednią wiadomość.
    if (error.message === "Network Error") {
        handledError.type = "NETWORK_ERROR";
        handledError.message = "Utracono połączenie z internetem.";
        return handledError;
    }

    // Wystąpił dowolny inny błąd, który nie został obsłużony.
    if (error instanceof Error) {
        handledError.message = error.message;
        return handledError;
    }

    return handledError;
}
